import React, { useState } from 'react';
import { createStyles, makeStyles } from '@mui/styles';
import classNames from 'classnames';
import StatItem from './StatItem';
import GroupMemberList from './GroupMemberList';
import { GroupMemberHookReturn } from '../../hooks/useGroupMemberHook';

const useStyles = makeStyles(() =>
    createStyles({
        groupItem: {
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid #E3E3FA'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
        },
        titleSection: {
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            flexWrap: 'wrap'
        },
        tagsContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
        },
        title: {
            fontSize: '1.25rem',
            fontWeight: 700,
            color: '#1F2152',
            margin: 0
        },
        phaseBadge: {
            padding: '4px 12px',
            borderRadius: '1000px',
            fontSize: '0.8rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            
        },
        greenBadge: {
            backgroundColor: '#E9FBF2',
            color: '#1B9757',
            border: '1px solid #1B975750'
        },
        redBadge: {
            backgroundColor: '#FEE7EC',
            color: '#A80A2D',
            border: '1px solid #A80A2D50'
        },
        blueBadge: {
            backgroundColor: '#F7F9FD',
            color: '#5E83E1',
            border: '1px solid #5E83E150'
        },
        viewMembersButton: {
            backgroundColor: 'transparent',
            border: 'none',
            color: '#7378E8',
            fontFamily: 'Manrope',
            fontSize: '16px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            '& i': {
                fontSize: '14px',
            }
        },
        statsGrid: {
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
            gap: '24px',
            flexWrap: 'wrap'
        },
        membersDivider: {
            margin: '24px 0',
            height: '2px',
            background: `repeating-linear-gradient(
                to right,
                #E3E3FA 0,
                #E3E3FA 12px,
                transparent 12px,
                transparent 24px
            )`,
            border: 'none'
        },
        membersSection: {
            padding: '20px',
            borderRadius: '8px',
            backgroundColor: '#FFFFFF',
            minHeight: '100px'
        }
    })
);

type TagType = 'green' | 'red' | 'blue';

type MemberGroupListItemProps = {
    groupId: number;
    title: string;
    tags: [string, TagType][];
    stats: [string, number][];
    onViewMembers?: () => void;
    hook: GroupMemberHookReturn;
};

const MemberGroupListItem: React.FC<MemberGroupListItemProps> = ({
    groupId,
    title,
    tags,
    stats,
    onViewMembers,
    hook
}) => {
    const classes = useStyles();
    const [showMembers, setShowMembers] = useState(false);

    const handleToggleMembers = () => {
        if (!showMembers) {
            // Only fetch when opening
            if (onViewMembers) {
                onViewMembers();
            }
        } else {
            // Clear members when closing
            hook.clearMembers(groupId);
        }
        setShowMembers(!showMembers);
    };

    return (
        <div className={classes.groupItem}>
            <div className={classes.header}>
                <div className={classes.titleSection}>
                    <h3 className={classes.title}>{title}</h3>
                    {/* Tags */}
                    <div className={classes.tagsContainer}>
                        {tags.map(([tagText, tagType], index) => (
                            <span 
                                key={index} 
                                                            className={classNames(
                                classes.phaseBadge,
                                tagType === 'green' ? classes.greenBadge : 
                                tagType === 'red' ? classes.redBadge : 
                                classes.blueBadge
                            )}
                            >
                                {tagText}
                            </span>
                        ))}
                    </div>
                </div>
                {/* View Members Button */}
                <button 
                    className={classes.viewMembersButton}
                    onClick={handleToggleMembers}
                >
                    {showMembers ? 'Hide Members' : 'View Members'}
                    <i className={`fas ${showMembers ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                </button>
            </div>
            
            {/* Stats Grid */}
            <div className={classes.statsGrid}>
                {stats.map(([label, value], index) => (
                    <StatItem key={index} label={label} value={value} />
                ))}
            </div>

            {/* Members Section */}
            {showMembers && (
                <>
                    <div className={classes.membersDivider} />
                    <GroupMemberList
                        groupId={groupId}
                        isVisible={showMembers}
                        hook={hook}
                    />
                </>
            )}
        </div>
    );
};

export default MemberGroupListItem; 